/**
 * simulatedVerifyProgress — time-driven progress for the verify-fix card.
 *
 * Verification is dominated by one opaque await (server capture + GPT-4o),
 * so the caller can only report coarse milestones (the dashboard reports a
 * single static 50%). Previously the bar froze at that milestone for the
 * whole run and looked dead. Instead, the in-progress view animates the bar
 * on a timer: from the latest milestone (the "base") it eases asymptotically
 * toward a 95% ceiling, fast at first and slowing as it approaches. It only
 * reaches 100% when the verify actually completes (the card swaps to the
 * result state and this unmounts).
 *
 * Mirrored 1:1 in
 * `packages/extension/src/content/verify/simulatedVerifyProgress.ts` — the
 * two VerificationCards are deliberately kept in sync. Change both.
 */
import { useEffect, useRef, useState } from 'react';

/** The timer alone never passes this; 100% means the verify actually finished. */
export const SIMULATED_PROGRESS_CEILING = 95;

/** Easing time constant: ~63% of the remaining gap closes per TAU. */
const TAU_MS = 12_000;

/** UI update cadence. */
const TICK_MS = 200;

/**
 * Pure curve: percent shown `elapsedMs` after the bar was last (re)based at
 * `basePercent`. Monotonic in elapsed time, clamped to [base, ceiling].
 */
export function simulatedVerifyPercent(elapsedMs: number, basePercent: number): number {
  const base = Math.min(Math.max(basePercent, 0), SIMULATED_PROGRESS_CEILING);
  const elapsed = Math.max(elapsedMs, 0);
  const eased =
    base + (SIMULATED_PROGRESS_CEILING - base) * (1 - Math.exp(-elapsed / TAU_MS));
  return Math.min(SIMULATED_PROGRESS_CEILING, Math.round(eased));
}

/**
 * Hook driving the displayed percent while a verify run is in flight.
 *
 * Mount it in a component that only exists during State B (the in-progress
 * view) so each run starts fresh. `floorPercent` is the caller's coarse
 * milestone; bumps re-base the easing curve so it restarts from the new
 * floor instead of waiting for the old curve to catch up. Never decreases.
 */
export function useSimulatedVerifyProgress(floorPercent: number): number {
  const [displayed, setDisplayed] = useState<number>(() =>
    simulatedVerifyPercent(0, floorPercent)
  );
  const baseRef = useRef<{ base: number; sinceMs: number }>({
    base: simulatedVerifyPercent(0, floorPercent),
    sinceMs: Date.now(),
  });

  // Re-base when the caller's milestone bumps.
  useEffect(() => {
    setDisplayed((prev) => {
      const base = Math.max(prev, simulatedVerifyPercent(0, floorPercent));
      baseRef.current = { base, sinceMs: Date.now() };
      return base;
    });
  }, [floorPercent]);

  useEffect(() => {
    const id = setInterval(() => {
      const { base, sinceMs } = baseRef.current;
      setDisplayed((prev) =>
        Math.max(prev, simulatedVerifyPercent(Date.now() - sinceMs, base))
      );
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);

  return displayed;
}
